import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@shared/lib/utils';

const Tabs = TabsPrimitive.Root;

const tabsListClassName =
  'inline-flex h-9 items-center justify-center gap-0 rounded-lg bg-accent p-[3px] text-base-foreground dark:bg-base-muted';

const tabsTriggerClassName = cn(
  'inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-2 py-1',
  'text-sm font-medium leading-5 text-base-foreground',
  'transition-[color,box-shadow,background-color,border-color] duration-fast ease-standard',
  'focus-visible:outline-none focus-visible:border focus-visible:border-[#A3A3A3] focus-visible:shadow-[0_0_0_3px_rgba(163,163,163,0.5)]',
  'disabled:pointer-events-none disabled:opacity-50',
  'data-[state=active]:bg-background data-[state=active]:text-base-foreground data-[state=active]:shadow-sm',
  'dark:data-[state=active]:border dark:data-[state=active]:border-white/15 dark:data-[state=active]:bg-white/[0.045] dark:data-[state=active]:text-foreground',
);

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List ref={ref} className={cn(tabsListClassName, className)} {...props} />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger ref={ref} className={cn(tabsTriggerClassName, className)} {...props} />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsBadge = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1',
      'text-xs font-semibold leading-4',
      'bg-base-primary text-base-primary-foreground shadow-sm',
      'dark:bg-[#5BF6FF] dark:text-[#0B0F14]',
      className,
    )}
    {...props}
  />
);
TabsBadge.displayName = 'TabsBadge';

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(163,163,163,0.5)]',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsBadge, TabsContent };
